import { useState, useEffect, useMemo } from "react";
import { Link, useSearch } from "wouter";
import { useQuery } from "@tanstack/react-query";
import { usePersonalizationTracker } from "@/hooks/usePersonalizationTracker";
import {
  Users,
  Globe,
  Mic,
  Newspaper,
  Rocket,
  ArrowRight,
  Sparkles,
  Crown,
  TrendingUp,
  Lightbulb,
  MessageSquare,
  Network,
  DollarSign,
  BookOpen,
  Quote,
  Star,
  CheckCircle2,
  Zap,
  Heart,
  Award,
  Settings2,
  ExternalLink,
  ChevronDown,
  Search
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Skeleton } from "@/components/ui/skeleton";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { useAuth } from "@/hooks/useAuth";
import { LandingEditProvider, EditableText, EditableButton, AdminEditToolbar } from "@/components/landing";
import { ProductLogo, productConfig } from "@/components/ProductLogos";
import { TestimonialDisplay } from "@/components/TestimonialDisplay";
import type { Article } from "@shared/schema";
import { useMediaCategories } from "@/hooks/useMediaCategories";
import { usePlatformTokens, DEFAULT_PLATFORM_STATS } from "@/hooks/usePlatformTokens";
import bookCoverImage from "@assets/Chief_in_Tech_Book_Cover_2025-2_1765655498601.png";

// Helper to get ecosystem data with dynamic tokens
function getEcosystemEnhanced(stats: typeof DEFAULT_PLATFORM_STATS) {
  return [
    {
      key: "capital" as const,
      href: "/capital",
      benefit: "Raise Capital",
      description: `Access ${stats.capitalConnected.value} investor network backing women-led ventures`,
      stat: stats.capitalConnected.value,
      statLabel: stats.capitalConnected.label,
      features: ["Investor matching", "Pitch preparation", "Deal flow access"],
    },
    {
      key: "media" as const,
      href: "/media",
      benefit: "Build Authority",
      description: `Publish insights reaching ${stats.monthlyReaders.value} tech decision-makers`,
      stat: stats.monthlyReaders.value,
      statLabel: stats.monthlyReaders.label,
      features: ["Thought leadership", "Executive interviews", "AI research briefs"],
    },
    {
      key: "network" as const,
      href: "/network",
      benefit: "Connect Strategically",
      description: `Get warm introductions to executives across ${stats.countries.value} countries`,
      stat: stats.techLeaders.value,
      statLabel: stats.techLeaders.label,
      features: ["Warm intros", "Office hours", "Advisory matching"],
    },
    {
      key: "summit" as const,
      href: "/summit",
      benefit: "Meet Virtual & In-Person",
      description: `Join ${stats.speakers.value} speakers and leaders at the premier tech summit`,
      stat: stats.speakers.value,
      statLabel: stats.speakers.label,
      features: ["Live networking", "Masterclasses", "Deal rooms"],
    },
    {
      key: "book" as const,
      href: "/book",
      benefit: "Learn From Leaders",
      description: `Discover strategies from ${stats.bookContributors.value} C-suite executives`,
      stat: stats.bookContributors.value,
      statLabel: "Executive Stories",
      features: ["Career playbooks", "Success frameworks", "Real strategies"],
    },
  ];
}

// Helper to get hero stats with dynamic tokens
function getHeroStats(stats: typeof DEFAULT_PLATFORM_STATS) {
  return [
    { icon: Users, value: stats.techLeaders.value, label: stats.techLeaders.label, description: "featured and engaged" },
    { icon: DollarSign, value: stats.capitalConnected.value, label: stats.capitalConnected.label, description: "Elevating women-led ventures" },
    { icon: Globe, value: stats.countries.value, label: stats.countries.label, description: "Global leadership network" },
    { icon: Mic, value: stats.speakers.value, label: stats.speakers.label, description: "Industry thought leaders" },
  ];
}

const whoWeServe = [
  {
    icon: Crown,
    role: "Executives",
    title: "C-suite leaders and board members",
    benefits: [
      "Visibility for board and advisory roles",
      "Global executive connections",
      "Featured in Thought leadership platform",
      "Executive search exposure"
    ],
    cta: "Explore Executive Opportunities",
    href: "/join/executive",
  },
  {
    icon: Rocket,
    role: "Founders",
    title: "Leaders building the future",
    benefits: [
      "Capital access through vetted investors",
      "Deal-readiness guidance",
      "Advisor and expert office hours",
      "Founder pitch opportunities"
    ],
    cta: "Explore Founder Programs",
    href: "/join/founder",
  },
  {
    icon: TrendingUp,
    role: "Investors",
    title: "VCs, angels, and LPs",
    benefits: [
      "Curated deal access",
      "Women-led and balanced teams",
      "Co-investor connections",
      "Portfolio support tools"
    ],
    cta: "Explore Investor Access",
    href: "/join/investor",
  },
  {
    icon: Lightbulb,
    role: "Advisors",
    title: "Industry experts and operators",
    benefits: [
      "Advisory role opportunities",
      "Office hours and expert sessions",
      "Knowledge sharing programs",
      "Advisory profile visibility"
    ],
    cta: "Explore Advisory",
    href: "/join/advisor",
  },
];

const fourPillars = [
  {
    icon: Newspaper,
    title: "Media",
    subtitle: "Thought Leadership and Insight",
    description: "Research, executive interviews, and industry analysis. Contribute to a publication reaching 100,000+ readers.",
    highlights: ["AI leadership briefs", "Executive interviews", "Research reports", "Contributor platform"],
    href: "/media",
    color: "bg-purple-500/10 text-purple-600 dark:text-purple-400",
  },
  {
    icon: Network,
    title: "Network",
    subtitle: "Strategic Connections",
    description: "Access warm introductions, expert office hours, and partner benefits. Programs are operated by WomenTech Network through EWIT.",
    highlights: ["Warm introductions", "Expert office hours", "Partner benefits", "Advisory access"],
    href: "/network",
    color: "bg-blue-500/10 text-blue-600 dark:text-blue-400",
  },
  {
    icon: MessageSquare,
    title: "Community",
    subtitle: "Leadership Programs",
    description: "Participate in structured leadership formats and events.",
    highlights: ["Leadership roundtables", "Ask Board sessions", "Innovation challenges", "Annual summit"],
    href: "/community",
    color: "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400",
  },
  {
    icon: DollarSign,
    title: "Capital",
    subtitle: "Funding and Growth",
    description: "Prepare for investment and connect with active investors backing women-led and balanced teams.",
    highlights: ["Deal readiness", "Investor dealroom access", "Pitch support", "Portfolio tools"],
    href: "/capital",
    color: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
  },
];


const defaultContent: Record<string, { anonymous: string; loggedIn: string }> = {
  hero_headline_1: {
    anonymous: "Where Tech Leaders",
    loggedIn: "Welcome Back, Leader",
  },
  hero_headline_2: {
    anonymous: "Shape the Future",
    loggedIn: "Shape the Future of Tech",
  },
  hero_subtext: {
    anonymous: "Featuring executives, founders, investors, and advisors building and funding the next era of technology.\n\nYour network. Your reach. Your next step.",
    loggedIn: "Continue connecting with top tech leaders, founders, and investors in your network.",
  },
  hero_button_1: {
    anonymous: JSON.stringify({ text: "Get Access", url: "/login" }),
    loggedIn: JSON.stringify({ text: "Go to Dashboard", url: "/home" }),
  },
  hero_button_2: {
    anonymous: JSON.stringify({ text: "Attend the Summit", url: "/events/chief-in-tech-summit" }),
    loggedIn: JSON.stringify({ text: "Explore Network", url: "/founders" }),
  },
  stat_0_value: { anonymous: DEFAULT_PLATFORM_STATS.techLeaders.value, loggedIn: DEFAULT_PLATFORM_STATS.techLeaders.value },
  stat_0_label: { anonymous: DEFAULT_PLATFORM_STATS.techLeaders.label, loggedIn: DEFAULT_PLATFORM_STATS.techLeaders.label },
  stat_0_desc: { anonymous: "featured and engaged", loggedIn: "featured and engaged" },
  stat_1_value: { anonymous: DEFAULT_PLATFORM_STATS.capitalConnected.value, loggedIn: DEFAULT_PLATFORM_STATS.capitalConnected.value },
  stat_1_label: { anonymous: DEFAULT_PLATFORM_STATS.capitalConnected.label, loggedIn: DEFAULT_PLATFORM_STATS.capitalConnected.label },
  stat_1_desc: { anonymous: "Elevating women-led ventures", loggedIn: "Elevating women-led ventures" },
  stat_2_value: { anonymous: DEFAULT_PLATFORM_STATS.countries.value, loggedIn: DEFAULT_PLATFORM_STATS.countries.value },
  stat_2_label: { anonymous: DEFAULT_PLATFORM_STATS.countries.label, loggedIn: DEFAULT_PLATFORM_STATS.countries.label },
  stat_2_desc: { anonymous: "Global leadership network", loggedIn: "Global leadership network" },
  stat_3_value: { anonymous: DEFAULT_PLATFORM_STATS.speakers.value, loggedIn: DEFAULT_PLATFORM_STATS.speakers.value },
  stat_3_label: { anonymous: DEFAULT_PLATFORM_STATS.speakers.label, loggedIn: DEFAULT_PLATFORM_STATS.speakers.label },
  stat_3_desc: { anonymous: "Industry thought leaders", loggedIn: "Industry thought leaders" },
  who_we_serve_title: {
    anonymous: "Who We Serve",
    loggedIn: "Your Community",
  },
  who_we_serve_subtitle: {
    anonymous: "A global leadership platform connecting roles with real outcomes.",
    loggedIn: "Connect with leaders across the ecosystem.",
  },
  pillars_title: {
    anonymous: "Four Pillars. One Platform.",
    loggedIn: "Your Ecosystem",
  },
  pillars_subtitle: {
    anonymous: "Everything to lead, connect, and grow across technology.",
    loggedIn: "Access all the resources designed for your success.",
  },
  testimonials_title: {
    anonymous: "Voices from the Community",
    loggedIn: "What Leaders Are Saying",
  },
  testimonials_subtitle: {
    anonymous: "Hear from executives, founders, and investors who are part of the movement.",
    loggedIn: "Stories from your network.",
  },
  book_title: {
    anonymous: "Break the Silicon Ceiling",
    loggedIn: "Break the Silicon Ceiling",
  },
  book_subtitle: {
    anonymous: "How Leaders Rise to Tech Leadership",
    loggedIn: "How Leaders Rise to Tech Leadership",
  },
  book_description: {
    anonymous: `The definitive guide for leaders navigating the path to tech leadership. Featuring insights from ${DEFAULT_PLATFORM_STATS.bookContributors.value} C-suite executives, practical strategies, and real stories of breaking barriers.`,
    loggedIn: "Your guide to navigating the path to tech leadership.",
  },
  insights_title: {
    anonymous: "Latest Insights",
    loggedIn: "Curated For You",
  },
  insights_subtitle: {
    anonymous: "Thought leadership and research from Chief in Tech and the EWIT community",
    loggedIn: "Your personalized feed based on your interests and connections",
  },
  cta_title: {
    anonymous: "Your Seat at the Table Awaits",
    loggedIn: "Expand Your Network",
  },
  cta_subtitle: {
    anonymous: `Join ${DEFAULT_PLATFORM_STATS.techLeaders.value} tech leaders who are done waiting for opportunities—and started creating them.`,
    loggedIn: "Discover new opportunities to grow your influence and make meaningful connections.",
  },
  cta_button_primary: {
    anonymous: JSON.stringify({ text: "Get Access", url: "/login" }),
    loggedIn: JSON.stringify({ text: "Explore Directory", url: "/founders" }),
  },
  cta_button_secondary: {
    anonymous: JSON.stringify({ text: "Attend the Summit", url: "/events/chief-in-tech-summit" }),
    loggedIn: JSON.stringify({ text: "View Profile", url: "/profile" }),
  },
  trust_headline: {
    anonymous: "As seen on",
    loggedIn: "As seen on",
  },
};

// Dynamic Ecosystem Section with rotating featured product
function EcosystemSection() {
  const [featuredIndex, setFeaturedIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const { platformStats } = usePlatformTokens();

  // Memoize ecosystem data to prevent recreation on each render
  const ecosystemEnhanced = useMemo(() => getEcosystemEnhanced(platformStats), [platformStats]);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (isPaused) return;

    const interval = setInterval(() => {
      setFeaturedIndex((prev) => (prev + 1) % ecosystemEnhanced.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isPaused, ecosystemEnhanced]);

  const featuredProduct = ecosystemEnhanced[featuredIndex];
  const otherProducts = ecosystemEnhanced.filter((_, i) => i !== featuredIndex);

  return (
    <section
      className="py-16 lg:py-24 bg-muted/30"
      data-testid="section-ecosystem"
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4">
            The Chief in Tech Ecosystem
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Everything you need to rise, connect, and lead in tech
          </p>
        </div>

        {/* Featured Product - Dynamically rotating */}
        <div className="mb-8">
          <Link href={featuredProduct.href}>
            <Card
              className="hover-elevate cursor-pointer group overflow-visible border-2 border-primary/20 bg-gradient-to-br from-card to-primary/5 transition-all duration-500"
              data-testid={`card-ecosystem-featured-${featuredProduct.key}`}
            >
              <CardContent className="p-8">
                <div className="grid lg:grid-cols-2 gap-8 items-center">
                  <div>
                    <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
                      Featured
                    </Badge>
                    <ProductLogo product={featuredProduct.key} variant="full" size="lg" />
                    <p className="text-lg text-muted-foreground mt-4 mb-6">
                      {featuredProduct.description}
                    </p>
                    <div className="flex flex-wrap gap-3">
                      {featuredProduct.features.map((feature, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <CheckCircle2 className="h-4 w-4 text-primary" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="flex justify-center lg:justify-end">
                    <div className="relative">
                      <div className="absolute -inset-4 bg-gradient-to-br from-[#4C1481]/20 to-[#14B8A6]/20 rounded-2xl blur-xl" />
                      <div className="relative bg-gradient-to-br from-[#4C1481] to-[#14B8A6] rounded-2xl p-8 text-white text-center min-w-[200px]">
                        <span className="text-5xl lg:text-6xl font-bold">{featuredProduct.stat}</span>
                        <p className="text-lg opacity-80 mt-2">{featuredProduct.statLabel}</p>
                        <Button className="mt-6 bg-white text-primary hover:bg-white/90" asChild>
                          <span>
                            Explore {productConfig[featuredProduct.key].name.replace("Chief in Tech ", "")}
                            <ArrowRight className="ml-2 h-4 w-4" />
                          </span>
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Link>
        </div>

        {/* Rotation indicator dots */}
        <div className="flex justify-center gap-2 mb-8">
          {ecosystemEnhanced.map((product, index) => (
            <button
              key={product.key}
              onClick={() => setFeaturedIndex(index)}
              className="relative flex items-center justify-center rounded-full transition-all duration-300 hover:bg-primary/5"
              style={{ minWidth: 44, minHeight: 44 }}
              aria-label={`View ${product.benefit}`}
              data-testid={`button-ecosystem-dot-${index}`}
            >
              <span className={`block rounded-full transition-all duration-300 ${index === featuredIndex
                ? 'w-8 h-2.5 bg-primary'
                : 'w-2.5 h-2.5 bg-primary/30'
                }`} />
            </button>
          ))}
        </div>

        {/* Other Products - Grid layout */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {otherProducts.map((product) => (
            <Link key={product.key} href={product.href} data-testid={`link-ecosystem-${product.key}`}>
              <Card
                className="h-full hover-elevate cursor-pointer group transition-all duration-300"
                data-testid={`card-ecosystem-${product.key}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-3 mb-3">
                    <ProductLogo product={product.key} variant="icon" size="md" />
                    <div className="text-right ml-auto">
                      <span className="text-xl font-bold text-primary">{product.stat}</span>
                    </div>
                  </div>
                  <h3 className="font-semibold mb-1 group-hover:text-primary transition-colors">
                    {product.benefit}
                  </h3>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {product.description}
                  </p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function LandingContent() {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const { getCategoryLabel } = useMediaCategories();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const showFrontPage = searchParams.get("front") === "true";

  // When admin uses ?front=true, show anonymous view
  const effectiveIsAuthenticated = showFrontPage ? false : isAuthenticated;

  // Use platform tokens for dynamic stats - memoized to prevent unnecessary re-renders
  const { platformStats } = usePlatformTokens();
  const stats = useMemo(() => getHeroStats(platformStats), [platformStats]);

  const [countrySearch, setCountrySearch] = useState("");
  const amazonUsLink = "https://www.amazon.com/Chief-Tech-Anna-Radulovski/dp/139429266X";
  const retailerGroups = [
    {
      region: "United States",
      retailers: [
        { name: "Amazon", url: "https://www.amazon.com/Chief-Tech-Anna-Radulovski/dp/139429266X" },
        { name: "Barnes & Noble", url: "https://www.barnesandnoble.com/w/chief-in-tech-anna-radulovski/1146703996?ean=9781394292660" },
        { name: "Books-A-Million", url: "https://www.booksamillion.com/p/Chief-Tech/Anna-Radulovski/9781394292660?id=9391105391744" },
        { name: "Powell's Books", url: "https://www.powells.com/book/chief-in-tech-9781394292660" },
      ]
    },
    {
      region: "United Kingdom",
      retailers: [
        { name: "Amazon UK", url: "https://www.amazon.co.uk/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X" },
        { name: "Waterstones", url: "https://www.waterstones.com/book/chief-in-tech/anna-radulovski/9781394292660" },
      ]
    },
    {
      region: "Canada",
      retailers: [
        { name: "Amazon Canada", url: "https://www.amazon.ca/Chief-Tech-Anna-Radulovski/dp/139429266X" },
      ]
    },
    {
      region: "Europe",
      retailers: [
        { name: "Austria - Amazon", url: "https://www.amazon.com.be/-/en/Anna-Radulovski/dp/139429266X/" },
        { name: "Belgium - Libristo", url: "https://www.libristo.eu/en/book/chief-in-tech_46681991" },
        { name: "France - Amazon", url: "https://www.amazon.fr/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Germany - Amazon", url: "https://www.amazon.de/-/en/Anna-Radulovski/dp/139429266X/" },
        { name: "Italy - Amazon", url: "https://www.amazon.it/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Netherlands - Amazon", url: "https://www.amazon.nl/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Poland - Amazon", url: "https://www.amazon.pl/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Spain - Amazon", url: "https://www.amazon.es/Chief-Tech-Anna-Radulovski/dp/139429266X" },
      ]
    },
    {
      region: "Asia-Pacific",
      retailers: [
        { name: "Australia - Amazon", url: "https://www.amazon.com.au/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "India - Amazon", url: "https://www.amazon.in/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Japan - Amazon", url: "https://www.amazon.co.jp/Chief-Tech-Anna-Radulovski/dp/139429266X" },
        { name: "Singapore - Amazon", url: "https://www.amazon.sg/Chief-Tech-Anna-Radulovski/dp/139429266X/" },
        { name: "UAE - Amazon", url: "https://www.amazon.ae/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
      ]
    },
    {
      region: "Latin America",
      retailers: [
        { name: "Mexico - Amazon", url: "https://www.amazon.com.mx/Chief-Tech-Breaking-Silicon-Ceiling/dp/139429266X/" },
        { name: "Brazil - Amazon", url: "https://www.amazon.com.br/dp/139429266X/" },
      ]
    },
  ];
  const filteredGroups = retailerGroups.map(group => ({
    ...group,
    retailers: group.retailers.filter(r =>
      r.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      group.region.toLowerCase().includes(countrySearch.toLowerCase())
    )
  })).filter(group => group.retailers.length > 0);

  // Fetch real articles from API
  const { data: articlesData, isLoading: articlesLoading } = useQuery<Article[]>({
    queryKey: ["/api/articles", { limit: 12 }],
    queryFn: () => fetch("/api/articles?limit=12").then(r => r.json()).then(d => Array.isArray(d) ? d : []),
  });

  // Track landing page view for analytics
  const { trackView } = usePersonalizationTracker();
  useEffect(() => {
    trackView("page", "landing");
  }, [trackView]);

  // Get latest 3 published articles for insights section
  const latestArticles = (articlesData || [])
    .filter(a => a.isPublished)
    .sort((a, b) => new Date(b.publishedAt || b.createdAt || 0).getTime() - new Date(a.publishedAt || a.createdAt || 0).getTime())
    .slice(0, 3);

  return (
    <div className="min-h-screen bg-background">
      <Header />

      {isAdmin && <AdminEditToolbar />}

      {/* Hero Section - Redesigned with stronger value proposition */}
      <section
        className="relative min-h-[60vh] flex items-center justify-center overflow-hidden bg-cit-hero"
        data-testid="section-hero"
      >
        {/* Animated geometric pattern overlay — opacity raised so it reads as purple, not flat black */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-72 h-72 border border-white/40 rounded-full animate-pulse" />
          <div className="absolute top-32 right-16 w-56 h-56 border border-white/30 rounded-full" />
          <div className="absolute bottom-24 left-1/4 w-40 h-40 border border-white/35 rotate-45" />
          <div className="absolute top-1/3 right-1/4 w-32 h-32 border border-white/25 rotate-12" />
          <div className="absolute bottom-1/3 right-12 w-64 h-64 border border-white/30 rounded-full" />
        </div>

        {/* Ambient glow effects — bumped up so gradient reads as deep purple */}
        <div className="absolute right-0 top-1/4 w-[500px] h-[500px] bg-[#AA63E3]/35 rounded-full blur-3xl" />
        <div className="absolute left-0 bottom-1/4 w-[400px] h-[400px] bg-[#542480]/55 rounded-full blur-3xl" />
        <div className="absolute left-1/2 top-0 w-[300px] h-[300px] bg-[#AA63E3]/30 rounded-full blur-2xl" />

        <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 text-center">
          {/* Trust badge */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <Badge className="bg-white/10 text-white border-white/20 backdrop-blur-sm px-4 py-1.5 text-sm">
              <Sparkles className="w-4 h-4 mr-2" />
              The Global Platform for Tech Leadership
            </Badge>
          </div>

          <h1
            className="font-heading text-4xl sm:text-5xl lg:text-7xl font-bold leading-tight mb-6 min-h-[120px] sm:min-h-[150px] lg:min-h-[200px]"
            data-testid="text-hero-headline"
          >
            <EditableText
              contentKey="hero_headline_1"
              as="span"
              className="text-white block min-h-[1.2em]"
            />
            <EditableText
              contentKey="hero_headline_2"
              as="span"
              className="text-cit-accent block mt-2 min-h-[1.2em]"
            />
          </h1>

          <EditableText
            contentKey="hero_subtext"
            as="p"
            className="text-lg sm:text-xl lg:text-2xl text-white/80 max-w-3xl mx-auto mb-10 leading-relaxed min-h-[60px] sm:min-h-[80px]"
            multiline
          />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <EditableButton
              contentKey="hero_button_1"
              size="lg"
              className="min-w-[200px] bg-[#8B3DC5] hover:bg-[#7A35B0] text-white border-[#8B3DC5]/50"
              defaultText="Get Access"
              defaultUrl="/login"
              data-testid="button-hero-primary"
            />
            <EditableButton
              contentKey="hero_button_2"
              size="lg"
              variant="outline"
              className="min-w-[200px] bg-white/10 text-white border-white/30 backdrop-blur-sm"
              defaultText="Attend the Summit"
              defaultUrl="/events/chief-in-tech-summit"
              data-testid="button-hero-secondary"
            />
          </div>

          {/* Quick role links */}
          <div className="flex flex-wrap items-center justify-center gap-3 text-sm text-white/60">
            <span>I am a:</span>
            <Link href="/join/executive" className="text-white/80 hover:text-white underline underline-offset-2" data-testid="link-role-executive">Executive</Link>
            <span className="text-white/40">|</span>
            <Link href="/join/founder" className="text-white/80 hover:text-white underline underline-offset-2" data-testid="link-role-founder">Founder</Link>
            <span className="text-white/40">|</span>
            <Link href="/join/investor" className="text-white/80 hover:text-white underline underline-offset-2" data-testid="link-role-investor">Investor</Link>
            <span className="text-white/40">|</span>
            <Link href="/join/advisor" className="text-white/80 hover:text-white underline underline-offset-2" data-testid="link-role-advisor">Advisor</Link>
          </div>
        </div>

        {/* Bottom gradient */}
        <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-background to-transparent" />
      </section>

      {/* Stats Section - Updated metrics */}
      <section
        className="py-16 lg:py-20 bg-background relative -mt-8"
        data-testid="section-stats"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
            {stats.map((stat, index) => (
              <Card
                key={stat.label}
                className="text-center hover-elevate border-none shadow-lg"
                data-testid={`card-stat-${index}`}
              >
                <CardContent className="pt-6 pb-6">
                  <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-secondary/10">
                    <stat.icon className="h-7 w-7 text-secondary" />
                  </div>
                  <EditableText
                    contentKey={`stat_${index}_value`}
                    as="div"
                    className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-1"
                  />
                  <EditableText
                    contentKey={`stat_${index}_label`}
                    as="div"
                    className="text-base font-semibold text-foreground mb-1"
                  />
                  <EditableText
                    contentKey={`stat_${index}_desc`}
                    as="div"
                    className="text-sm text-muted-foreground"
                  />
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section
        className="py-16 lg:py-24 bg-muted/30"
        data-testid="section-who-we-serve"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <EditableText
              contentKey="who_we_serve_title"
              as="h2"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            />
            <EditableText
              contentKey="who_we_serve_subtitle"
              as="p"
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
              multiline
            />
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {whoWeServe.map((segment, index) => (
              <Card
                key={segment.role}
                className="h-full hover-elevate group relative overflow-visible flex flex-col"
                data-testid={`card-segment-${segment.role.toLowerCase()}`}
              >
                <CardHeader className="pb-4">
                  <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-secondary/10 group-hover:bg-secondary/20 transition-colors">
                    <segment.icon className="h-7 w-7 text-secondary" />
                  </div>
                  <Badge variant="outline" className="w-fit mb-2 text-xs">
                    {segment.role}
                  </Badge>
                  <CardTitle className="text-lg leading-snug">
                    {segment.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-0 flex-1 flex flex-col">
                  <ul className="space-y-2 mb-6 flex-1">
                    {segment.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="h-4 w-4 text-secondary shrink-0 mt-0.5" />
                        <span>{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <Button variant="outline" size="sm" className="w-full mt-auto" asChild data-testid={`button-join-${segment.role.toLowerCase()}`}>
                    <Link href={segment.href}>
                      {segment.cta}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Four Pillars Section - Redesigned */}
      <section
        className="py-16 lg:py-24 bg-background"
        data-testid="section-pillars"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <EditableText
              contentKey="pillars_title"
              as="h2"
              className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4"
            />
            <EditableText
              contentKey="pillars_subtitle"
              as="p"
              className="text-lg text-muted-foreground max-w-2xl mx-auto"
              multiline
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {fourPillars.map((pillar, index) => (
              <Link key={pillar.title} href={pillar.href} data-testid={`link-pillar-${pillar.title.toLowerCase()}`}>
                <Card
                  className="h-full hover-elevate cursor-pointer group"
                  data-testid={`card-pillar-${pillar.title.toLowerCase()}`}
                >
                  <CardContent className="p-6 lg:p-8">
                    <div className="flex items-start gap-4">
                      <div className={`shrink-0 flex h-14 w-14 items-center justify-center rounded-xl ${pillar.color} group-hover:scale-105 transition-transform`}>
                        <pillar.icon className="h-7 w-7" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-heading text-xl font-bold text-foreground mb-1">
                          {pillar.title}
                        </h3>
                        <p className="text-sm text-secondary font-medium mb-3">
                          {pillar.subtitle}
                        </p>
                        <p className="text-muted-foreground text-sm mb-4">
                          {pillar.description}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {pillar.highlights.map((highlight) => (
                            <Badge key={highlight} variant="secondary" className="text-xs">
                              {highlight}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="mt-4 flex items-center text-secondary font-medium text-sm group-hover:translate-x-1 transition-transform">
                      Explore {pillar.title}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Our Ecosystem Section - Dynamic Rotating Featured */}
      <EcosystemSection />

      {/* Social Proof / Testimonials Section */}
      <section
        className="py-16 sm:py-20 bg-muted/30"
        data-testid="section-testimonials"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 relative">
            {isAdmin && (
              <Link href="/admin/testimonials" className="absolute right-0 top-0">
                <Button
                  size="sm"
                  variant="outline"
                  data-testid="button-admin-edit-testimonials"
                >
                  <Settings2 className="h-4 w-4 mr-2" />
                  Edit Testimonials
                </Button>
              </Link>
            )}
            <EditableText
              contentKey="testimonials_title"
              as="h2"
              className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-4"
            />
            <EditableText
              contentKey="testimonials_subtitle"
              as="p"
              className="text-lg text-muted-foreground"
            />
          </div>
          <TestimonialDisplay pageSlug="/" sectionId="social-proof" />
        </div>
      </section>

      {/* Book Feature Section */}
      <section
        className="py-16 lg:py-24 bg-background"
        data-testid="section-book"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12 items-center">
            {/* Book Cover */}
            <div className="flex justify-start order-2 lg:order-1 lg:col-span-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-br from-[#AA63E3]/20 to-[#4A2573]/20 rounded-2xl blur-2xl" />
                <img
                  src={bookCoverImage}
                  alt="Break the Silicon Ceiling - Chief in Tech Book"
                  width={320}
                  height={480}
                  className="relative w-64 lg:w-80"
                  loading="lazy"
                  data-testid="img-book-cover"
                />
              </div>
            </div>

            {/* Book Content */}
            <div className="order-1 lg:order-2 lg:col-span-2">
              <Badge variant="secondary" className="mb-4">
                <Award className="w-3 h-3 mr-1" />
                Amazon Bestseller
              </Badge>

              <EditableText
                contentKey="book_title"
                as="h2"
                className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-2"
              />
              <EditableText
                contentKey="book_subtitle"
                as="h3"
                className="text-xl text-secondary font-medium mb-6"
              />
              <EditableText
                contentKey="book_description"
                as="p"
                className="text-muted-foreground text-lg mb-8 leading-relaxed"
                multiline
              />

              <div className="flex flex-wrap gap-4 mb-8">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="h-5 w-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  <span className="text-sm text-muted-foreground">5 out of 5 stars on Amazon</span>
                </div>
              </div>

              <div className="space-y-3 mb-8">
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-secondary" />
                  <span>{platformStats.bookContributors.value} C-Suite executive interviews</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-secondary" />
                  <span>Actionable frameworks for career advancement</span>
                </div>
                <div className="flex items-center gap-3 text-sm">
                  <CheckCircle2 className="h-5 w-5 text-secondary" />
                  <span>Real stories of breaking barriers</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-4">
                <div className="flex">
                  <Button asChild size="lg" className="rounded-r-none" data-testid="button-buy-book">
                    <a href={amazonUsLink} target="_blank" rel="noopener noreferrer">
                      Order on Amazon.com
                      <ExternalLink className="ml-2 h-4 w-4" />
                    </a>
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="lg" className="rounded-l-none border-l border-primary-foreground/20 px-2" data-testid="button-landing-region-dropdown">
                        <Globe className="h-4 w-4 mr-1" />
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-64">
                      <div className="p-2">
                        <div className="relative">
                          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                          <Input
                            placeholder="Search country or retailer..."
                            value={countrySearch}
                            onChange={(e) => setCountrySearch(e.target.value)}
                            className="pl-8"
                            data-testid="input-landing-country-search"
                          />
                        </div>
                      </div>
                      <div className="max-h-80 overflow-y-auto">
                        {filteredGroups.map((group, groupIdx) => (
                          <div key={group.region}>
                            {groupIdx > 0 && <DropdownMenuSeparator />}
                            <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                              {group.region}
                            </DropdownMenuLabel>
                            {group.retailers.map((retailer) => (
                              <DropdownMenuItem
                                key={`landing-${group.region}-${retailer.name}`}
                                asChild
                                data-testid={`dropdown-landing-${retailer.name.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <a href={retailer.url} target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                                  <span className="text-sm">{retailer.name}</span>
                                </a>
                              </DropdownMenuItem>
                            ))}
                          </div>
                        ))}
                        {filteredGroups.length === 0 && (
                          <div className="px-2 py-4 text-center text-sm text-muted-foreground">
                            No results found
                          </div>
                        )}
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
                <Button size="lg" variant="outline" asChild data-testid="button-book-details">
                  <Link href="/book">
                    Explore the Book
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Content Preview Section */}
      <section
        className="py-16 lg:py-24 bg-muted/30"
        data-testid="section-insights"
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <div>
              <EditableText
                contentKey="insights_title"
                as="h2"
                className="font-heading text-3xl sm:text-4xl font-bold text-foreground mb-2"
              />
              <EditableText
                contentKey="insights_subtitle"
                as="p"
                className="text-muted-foreground"
              />
            </div>
            <Button variant="outline" asChild data-testid="button-view-all-insights">
              <Link href="/media">
                View All
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Article Cards */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {articlesLoading ? (
              // Loading skeleton
              [1, 2, 3].map((i) => (
                <Card key={i} className="flex flex-col">
                  <CardHeader>
                    <div className="flex items-center gap-2 mb-2">
                      <Skeleton className="h-5 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <Skeleton className="h-6 w-full" />
                    <Skeleton className="h-6 w-3/4 mt-1" />
                  </CardHeader>
                  <CardContent>
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3" />
                  </CardContent>
                </Card>
              ))
            ) : latestArticles.length > 0 ? (
              latestArticles.map((article) => (
                <Link href={`/media/${article.slug}`} key={article.id}>
                  <Card
                    className="hover-elevate cursor-pointer group h-full"
                    data-testid={`card-insight-${article.id}`}
                  >
                    <CardHeader>
                      <div className="flex items-center gap-2 mb-2 flex-wrap">
                        <Badge variant="secondary" className="no-default-active-elevate">
                          {getCategoryLabel(article.category)}
                        </Badge>
                        <span className="text-sm text-muted-foreground">
                          {article.publishedAt
                            ? new Date(article.publishedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                            : 'Draft'}
                        </span>
                      </div>
                      <CardTitle className="text-lg leading-snug group-hover:text-secondary transition-colors line-clamp-2">
                        {article.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-base line-clamp-2">
                        {article.excerpt}
                      </CardDescription>
                      <div className="mt-4 flex items-center text-secondary font-medium text-sm">
                        Read more
                        <ArrowRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))
            ) : (
              // Empty state
              <div className="col-span-3 text-center py-12 text-muted-foreground">
                No articles available yet. Check back soon for fresh insights!
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Final CTA Section - Redesigned with urgency */}
      <section
        className="py-20 lg:py-28 bg-primary relative overflow-hidden"
        data-testid="section-cta"
      >
        {/* Background decorations */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-[#AA63E3] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#4A2573] rounded-full blur-3xl" />
        </div>

        <div className="relative mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          {/* Urgency badge */}
          <Badge className="bg-white/20 text-white border-white/20 mb-6">
            <Zap className="w-3 h-3 mr-1" />
            Limited Early Access Available
          </Badge>

          <EditableText
            contentKey="cta_title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4"
          />
          <EditableText
            contentKey="cta_subtitle"
            as="p"
            className="text-lg lg:text-xl text-white/90 mb-10 max-w-2xl mx-auto"
            multiline
          />

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <EditableButton
              contentKey="cta_button_primary"
              size="lg"
              className="min-w-[220px] bg-white text-primary border-white"
              defaultText="Get Access"
              defaultUrl="/login"
              data-testid="button-cta-primary"
            />
            <EditableButton
              contentKey="cta_button_secondary"
              size="lg"
              variant="outline"
              className="min-w-[220px] bg-white/10 backdrop-blur-xl text-white border-white/30"
              defaultText="Attend the Summit"
              defaultUrl="/events/chief-in-tech-summit"
              data-testid="button-cta-secondary"
            />
          </div>

          {/* Value props */}
          <div className="grid sm:grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <Users className="h-6 w-6 text-white" />
              </div>
              <p className="text-white/80 text-sm">{platformStats.techLeaders.value} vetted leaders</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <Globe className="h-6 w-6 text-white" />
              </div>
              <p className="text-white/80 text-sm">Network spanning {platformStats.countries.value} countries</p>
            </div>
            <div className="flex flex-col items-center text-center">
              <div className="h-12 w-12 rounded-full bg-white/10 flex items-center justify-center mb-3">
                <Heart className="h-6 w-6 text-white" />
              </div>
              <p className="text-white/80 text-sm">Built for leaders who act, not wait</p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer - DO NOT CHANGE */}
      <Footer />
    </div>
  );
}

export default function Landing() {
  const { user, isAuthenticated } = useAuth();
  const isAdmin = user?.isAdmin ?? false;
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  const showFrontPage = searchParams.get("front") === "true";

  // When admin uses ?front=true, show anonymous view
  const effectiveIsAuthenticated = showFrontPage ? false : isAuthenticated;

  return (
    <LandingEditProvider
      pageId="home"
      isAdmin={isAdmin}
      isLoggedIn={effectiveIsAuthenticated}
      defaultContent={defaultContent}
    >
      <LandingContent />
    </LandingEditProvider>
  );
}
